import { useEffect, useRef } from 'react';

import { Modal } from './Modal';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel?: string;
  pending?: boolean;
  pendingLabel?: string;
  tone?: 'default' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  pending = false,
  pendingLabel,
  tone = 'default',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const viewRef = useRef({ title, description, confirmLabel, cancelLabel, pendingLabel, tone });
  if (open) viewRef.current = { title, description, confirmLabel, cancelLabel, pendingLabel, tone };
  const view = viewRef.current;

  useEffect(() => {
    if (!open) return;
    cancelRef.current?.focus();
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={() => { if (!pending) onCancel(); }}
      alert
      labelledBy="confirm-dialog-title"
      describedBy="confirm-dialog-description"
      className="confirm-modal"
    >
      <span className={`confirm-modal-icon${view.tone === 'danger' ? ' danger' : ''}`} aria-hidden="true">!</span>
      <h2 id="confirm-dialog-title">{view.title}</h2>
      <p id="confirm-dialog-description">{view.description}</p>
      <div className="bed-modal-actions confirm-modal-actions">
        <button ref={cancelRef} className="button button-secondary" type="button" onClick={onCancel} disabled={pending}>
          {view.cancelLabel}
        </button>
        <button
          className={`button ${view.tone === 'danger' ? 'button-danger' : 'button-primary'}`}
          type="button"
          onClick={onConfirm}
          disabled={pending}
        >
          {pending ? (view.pendingLabel ?? view.confirmLabel) : view.confirmLabel}
        </button>
      </div>
    </Modal>
  );
};
